import { DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import localeEsCl from '@angular/common/locales/es-CL';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule } from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { NgxSpinnerModule } from 'ngx-spinner';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { appReducers } from './app.reducer';
import { AuthEffects } from './auth/auth.effects';
import { AuthModule } from './auth/auth.module';
import { DashboardModule } from './dashboard/dashboard.module';
import { RetailModule } from './retail/retail.module';
import { CognitoService } from './auth/services/cognito.service';
import { HttpInterceptorProviders } from './shared/interceptors/interceptor.provider';
import { LoadingInterceptor } from './shared/utils/loading.interceptor';
import { AlertService } from './shared/services/alert.service';

/**
 * Engancha el portero de modales antes que nada.
 *
 * Va primero de los inicializadores a propósito: `initializeAuth` puede
 * terminar en el corte por edad de sesión, que abre su propio aviso. Si el
 * portero no estuviera puesto todavía, ese aviso quedaría afuera de la cola y
 * el siguiente popup lo borraría sin resolverle la promesa.
 */
function initializeAlertas(alertService: AlertService) {
  return () => alertService.instalar();
}

function initializeAuth(cognitoService: CognitoService) {
  return () => {
    const authPromise = cognitoService.getSession()
      .then((session: any) => session ? cognitoService.getUser() : undefined)
      .catch(() => {});

    const timeout = new Promise<void>(resolve => setTimeout(resolve, 3000));

    return Promise.race([authPromise, timeout]);
  };
}

registerLocaleData(localeEn, 'en-US');
// es-CL solo para los pipes que lo piden explícito (panel de tasas): el
// LOCALE_ID global sigue en en-US.
registerLocaleData(localeEsCl, 'es-CL');

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AuthModule,
    HttpClientModule,
    RetailModule,
    DashboardModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    NgxSpinnerModule,
    StoreModule.forRoot(appReducers),
    EffectsModule.forRoot([AuthEffects]),
    NgIdleKeepaliveModule.forRoot(),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: !environment.production, // Restrict extension to log-only mode
    }),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideMessaging(() => getMessaging()),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAlertas,
      deps: [AlertService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      deps: [CognitoService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    DatePipe,
    { provide: LOCALE_ID, useValue: 'en-US' },
    ...HttpInterceptorProviders,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {}
